import { ApprovalStatus, VisitPurpose, VisitStatus } from "src/api/visitor/visitor-types";
import i18n from "src/i18n";
import type {
  VisitorApprovalStatusBadgeOptions,
  VisitorAccessLogFields,
  VisitorHistoryBadgeConfig,
  VisitorHistoryCardStateConfig,
  VisitorHistoryCardStateOptions,
  VisitorVisitStatusBadgeOptions,
} from "../types/visitorHistoryStatus";

// label 필드에는 i18n 키를 저장하고, 표시 직전(getter 호출 시점)에 resolve* 헬퍼로 번역한다.
// 모듈 로드 시점에 번역하면 언어 전환에 반응하지 못하므로, 키만 보관한다.
const badge = (labelKey: string, tone: VisitorHistoryBadgeConfig["tone"]): VisitorHistoryBadgeConfig => ({ label: labelKey, tone });

const dimmedCardState = (labelKey: string): VisitorHistoryCardStateConfig => ({ label: labelKey, tone: "red", isDimmed: true });

const translate = (key: string) => i18n.t(key, { ns: "visitor" });

const resolveBadge = (config: VisitorHistoryBadgeConfig): VisitorHistoryBadgeConfig =>
  config.label === "-" ? config : { ...config, label: translate(config.label) };

const resolveCardState = (config: VisitorHistoryCardStateConfig): VisitorHistoryCardStateConfig => ({ ...config, label: translate(config.label) });

const EMPTY_BADGE = badge("-", "gray");

const VISIT_STATUS_BADGE = {
  applyComplete: badge("badge.applyComplete", "blue"),
  scheduled: badge("badge.scheduled", "mint"),
  inProgress: badge("badge.inProgress", "mint"),
  paused: badge("badge.paused", "gray"),
  complete: badge("badge.complete", "gray"),
  missed: badge("notVisited", "gray"),
  canceled: badge("badge.canceled", "red"),
};

const APPROVAL_STATUS_BADGE = {
  pending: badge("badge.approvalPending", "blue"),
  complete: badge("badge.approvalComplete", "mint"),
  auto: badge("autoApproved", "mint"),
  rejected: badge("badge.rejected", "red"),
};

const VISITOR_VISIT_PURPOSE_LABEL_CONFIG: Record<VisitPurpose, string> = {
  [VisitPurpose.WORK_MEETING]: "purpose.workMeeting",
  [VisitPurpose.FRIEND_VISIT]: "purpose.friendVisit",
  [VisitPurpose.PACKAGE_DELIVERY]: "purpose.packageDelivery",
  [VisitPurpose.FOOD_DELIVERY]: "purpose.foodDelivery",
  [VisitPurpose.ETC]: "purpose.etc",
};

const isVisitPurpose = (visitPurpose: string): visitPurpose is VisitPurpose => {
  return visitPurpose in VISITOR_VISIT_PURPOSE_LABEL_CONFIG;
};

const CARD_STATE = {
  rejected: dimmedCardState("badge.rejectedCard"),
  canceled: dimmedCardState("badge.canceledCard"),
};

const VISITOR_VISIT_STATUS_BADGE_CONFIG: Record<VisitStatus, VisitorHistoryBadgeConfig> = {
  [VisitStatus.VISIT_PENDING]: VISIT_STATUS_BADGE.applyComplete,
  [VisitStatus.VISIT_USE]: VISIT_STATUS_BADGE.scheduled,
  [VisitStatus.VISIT_PAUSE]: VISIT_STATUS_BADGE.paused,
  [VisitStatus.VISIT_CLOSE]: VISIT_STATUS_BADGE.complete,
  [VisitStatus.VISIT_DELETE]: VISIT_STATUS_BADGE.canceled,
};

const VISITOR_VISIT_STATUS_CARD_STATE_CONFIG: Partial<Record<VisitStatus, VisitorHistoryCardStateConfig>> = {
  [VisitStatus.VISIT_DELETE]: CARD_STATE.canceled,
};

const VISITOR_APPROVAL_STATUS_BADGE_CONFIG: Record<ApprovalStatus, VisitorHistoryBadgeConfig> = {
  [ApprovalStatus.APPROVAL_PENDING]: APPROVAL_STATUS_BADGE.pending,
  [ApprovalStatus.APPROVAL_COMPLETE]: APPROVAL_STATUS_BADGE.complete,
  [ApprovalStatus.APPROVAL_AUTO]: APPROVAL_STATUS_BADGE.auto,
  [ApprovalStatus.APPROVAL_REJECT]: APPROVAL_STATUS_BADGE.rejected,
};

const isApprovalRejected = (approvalStatus?: ApprovalStatus | null) => {
  return approvalStatus === ApprovalStatus.APPROVAL_REJECT;
};

export const getVisitorHasAccessLog = (visitor?: VisitorAccessLogFields) => {
  if (!visitor) return false;

  return typeof visitor.accessUpdatedAt === "string" && visitor.accessUpdatedAt !== "";
};

export const getVisitorVisitStatusBadgeConfig = (visitStatus?: VisitStatus | null, options: VisitorVisitStatusBadgeOptions = {}) => {
  const config = ((): VisitorHistoryBadgeConfig => {
    if (options.isDeleted === true) return VISIT_STATUS_BADGE.canceled;

    if (!visitStatus) return EMPTY_BADGE;

    // 방문상태는 방문 처리상태와 출입기록 존재 여부를 조합해서 표시한다.
    if (visitStatus === VisitStatus.VISIT_USE && options.hasAccessLog) {
      return VISIT_STATUS_BADGE.inProgress;
    }

    if (visitStatus === VisitStatus.VISIT_CLOSE && options.hasAccessLog === false) {
      return VISIT_STATUS_BADGE.missed;
    }

    return VISITOR_VISIT_STATUS_BADGE_CONFIG[visitStatus];
  })();

  return resolveBadge(config);
};

export const getVisitorApprovalStatusBadgeConfig = (options: VisitorApprovalStatusBadgeOptions) => {
  const { approvalStatus } = options;

  if (!approvalStatus) return resolveBadge(EMPTY_BADGE);

  return resolveBadge(VISITOR_APPROVAL_STATUS_BADGE_CONFIG[approvalStatus] || EMPTY_BADGE);
};

export const getVisitorVisitStatusLabel = (visitStatus?: VisitStatus | null, options?: VisitorVisitStatusBadgeOptions) => {
  return getVisitorVisitStatusBadgeConfig(visitStatus, options).label;
};

export const getVisitorApprovalStatusLabel = (options: VisitorApprovalStatusBadgeOptions) => {
  return getVisitorApprovalStatusBadgeConfig(options).label;
};

export const getVisitorVisitPurposeLabel = (visitPurpose?: string | null, visitPurposeDescription?: string | null) => {
  if (!visitPurpose) {
    return "-";
  }

  if (!isVisitPurpose(visitPurpose)) {
    return visitPurpose;
  }

  const visitPurposeLabel = translate(VISITOR_VISIT_PURPOSE_LABEL_CONFIG[visitPurpose]);

  if (visitPurpose !== VisitPurpose.ETC) {
    return visitPurposeLabel;
  }

  return `${visitPurposeLabel} (${visitPurposeDescription || "-"})`;
};

export const getVisitorHistoryCardState = ({ approvalStatus, memberApprovalStatus, visitStatus }: VisitorHistoryCardStateOptions) => {
  if (visitStatus && VISITOR_VISIT_STATUS_CARD_STATE_CONFIG[visitStatus]) {
    return resolveCardState(VISITOR_VISIT_STATUS_CARD_STATE_CONFIG[visitStatus]!);
  }

  if (isApprovalRejected(memberApprovalStatus) || isApprovalRejected(approvalStatus)) {
    return resolveCardState(CARD_STATE.rejected);
  }
};
